import React from 'react';
import ChonkyFileBrowser, {
	renderFolderIcon,
} from './Chonky/ChonkyFileBrowser';
import {
	ChonkyActions,
	ChonkyIconName,
} from 'chonky';
import {
	getThumbnailPath,
	getSlideThumbnailURL,
} from './Chonky/lib';
import {
	isContentManager,
} from 'Vulture/Session';
import {
	shouldSkipSlide,
	getURLSlideThumbnailPath,
	urlSlideFallbackIcon,
} from './utils/slides';
import style from '../Vulture/Chonky/ChonkyFileBrowser.module.css';
import Img from 'wrapper/Img';

const getSlideThumbnailPath = (slide) => {
	if (slide?.kind === 'html') {
		// URL slide: the file IS the URL — no S3 thumbnail exists (DEV-12028).
		return getURLSlideThumbnailPath(slide.file);
	}

	return getSlideThumbnailURL(slide?.file ? slide.file : slide?.path, slide?.kind);
};

// Deck-list thumbnail with a one-shot fallback (URL slides only). State-guarded so a
// failing fallback can't re-trigger itself — React's onError ignores `e.target.onerror`.
const DeckThumbnail = ({
	url,
	fallbackUrl,
	alt,
}) => {
	const [ src, setSrc, ] = React.useState(url);

	React.useEffect(() => {
		setSrc(url);
	}, [ url, ]);

	return (
		<Img
			src={src}
			alt={alt}
			className={style.slideDeckThumbnail}
			onError={() => {
				if (fallbackUrl && src !== fallbackUrl) {
					setSrc(fallbackUrl);
				}
			}}
		/>
	);
};

class SlideDeckTableView extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			slideDecks:    props.slideDecks,
			selectedDecks: [],
			searchQuery:   '',
		};
	}

	componentDidUpdate(prevProps) {
		// If slideDecks prop changes, update state
		if (prevProps.slideDecks !== this.props.slideDecks) {
			this.setState({
				slideDecks:    this.props.slideDecks,
				selectedDecks: [],
			});
		}
	}

	handleSearch = (e) => {
		this.setState({
			searchQuery: e.target.value,
		});
	}

	clearSearch = () => {
		this.setState({
			searchQuery: '',
		});
	}

	getFilteredDecks = () => {
		const {
			slideDecks,
			searchQuery,
		} = this.state;

		// Filter by search query only - let VultureTable handle sorting
		let filteredDecks = slideDecks.filter((deck) => {
			return deck.name.toLowerCase().includes(searchQuery.toLowerCase());
		}
		);

		return filteredDecks;
	}

	render() {
		const filteredDecks = this.getFilteredDecks();

		const chonkyFiles = filteredDecks.map((deck) => {
			const totalSlots = Array.isArray(deck.slides) ? deck.slides.reduce((count, slide) => {
				return count + (slide.folder_slots || 1);
			}, 0) : 0;

			const activeSlots = Array.isArray(deck.slides) ?
				deck.slides.reduce((count, slide) => {
					if (slide.scheduled === false) {
						return count + (slide.folder_slots || 1);
					}
					if (slide.scheduled === true && !shouldSkipSlide(slide)) {
						return count + (slide.folder_slots || 1);
					}
					return count;
				}, 0) : 0;

			let thumbnails = [];
			if (Array.isArray(deck.slides) && deck.slides.length > 0) {
				const orderedSlides = deck.slides.map(obj => {
					let isActive = false;

					if (obj.scheduled === false) {
						isActive = true;
					} else if (obj.scheduled === true && !shouldSkipSlide(obj)) {
						isActive = true;
					}

					if (obj.slide) {
						return {
							...obj.slide,
							isFromLibrary: false,
							isActive,
							order:         obj.order,
						};
					} else if (obj.gallery_file) {
						return {
							...obj.gallery_file,
							isFromLibrary: true,
							isActive,
							order:         obj.order,
						};
					}

					return {
						...obj,
						isFromLibrary: false,
						isActive,
						order:         obj.order,
					};

				}).filter(Boolean)
					.sort((a, b) => {
						return a.order - b.order;
					});

				const thumbnailsWithoutInactive = orderedSlides.filter((slide) => {
					return slide.isActive;
				});

				thumbnails = thumbnailsWithoutInactive.slice(0, 3).map((slide) => {
					let url = '';
					if (slide.folder_id) {
						return {
							isFolder: true,
							url:      '',
						};
					}

					if (slide.isFromLibrary) {
						url = getThumbnailPath(slide.file ? slide.file : slide.path);
					} else {
						url = getSlideThumbnailPath(slide);
					}

					return {
						isFolder:    false,
						url,
						// Only URL slides get a fallback: a missing favicon → generic url icon.
						fallbackUrl: slide.kind === 'html' ? urlSlideFallbackIcon : '',
					};
				});
			}

			// A deck is inactive if it has slides but none are currently active
			const isInactive = totalSlots > 0 && activeSlots === 0;

			return {
				id:    deck.uuid,
				name:  deck.name,
				isDir: false,
				activeSlots,
				totalSlots,
				thumbnails,
				isInactive,
			};
		});

		const chonkyFileMap = {
			root: {
				id:          'root',
				name:        'Slide Decks',
				isDir:       true,
				childrenIds: chonkyFiles.map((f) => {
					return f.id;
				}),
				parentId: null,
			},
			...Object.fromEntries(chonkyFiles.map((f) => {
				return [ f.id, f, ];
			})),
		};

		const injectedColumns = [
			{
				id:       'name',
				label:    'Name',
				accessor: (f) => {
					return f.name;
				},
			},
			{
				id:    'thumbnail',
				label: (<div style={{
					textAlign:    'center',
					width:        '100%',
					paddingRight: '15px',
				}}>Thumbnails</div>),
				center:      true,
				notSortable: true,
				accessor:    (f) => {
					if (!f.thumbnails || f.thumbnails.length === 0) return null;
					return (
						<div className={style.thumbnailContainer}>
							{f.thumbnails.map(({
								isFolder,
								url,
								fallbackUrl,
							}, idx) => {
								if (isFolder) {
									return (
										<div className={style.slideDeckThumbnail} key={`folder${idx + 1}`}>
											{renderFolderIcon(48)}
										</div>
									);
								}

								return (
									<DeckThumbnail
										key={`${idx}-${url}`}
										url={url}
										fallbackUrl={fallbackUrl}
										alt={`thumbnail${idx + 1}`}
									/>
								);
							})}
						</div>
					);
				},
			},
			{
				id:       'activeSlots',
				label:    'Active Slots (Current)',
				center:   true,
				accessor: (f) => {
					return f.activeSlots !== undefined ? f.activeSlots : 0;
				},
			},
			{
				id:       'totalSlots',
				label:    'Total Slots',
				center:   true,
				accessor: (f) => {
					return f.totalSlots !== undefined ? f.totalSlots : 0;
				},
			},
		];

		// Check if user is a content manager
		const userIsContentManager = isContentManager();

		// Only show actions if user is NOT a content manager
		const injectedActions = userIsContentManager ? [] : [
			{
				id:      'add_deck',
				name:    'Add Deck',
				icon:    'clone',
				handler: () => {
					if (this.props.createNewDeck) this.props.createNewDeck();
				},
			},
			{
				id:      'delete_selected_decks',
				name:    'Delete Selected',
				icon:    ChonkyIconName.trash,
				handler: ({
					selection,
				}) => {
					if (!selection || selection.length === 0) return;
					if (this.props.deleteSelectedDecks) {
						this.props.deleteSelectedDecks(selection);
					}
				},
			},
		];

		return (
			<>
				<div
					style={{
						padding: '8px',
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						minHeight: 0,
					}}>
					<ChonkyFileBrowser
						mode="slideDecks"
						app={this.props.app}
						readOnly={true}
						addSelectAllToggleAction={!userIsContentManager}
						accountUUID={this.props.accountUUID}
						injectedFileMap={chonkyFileMap}
						injectedColumns={injectedColumns}
						injectedActions={injectedActions}
						hideEdit
						hideEditAndDeleteButtons={userIsContentManager}
						onInjectedFileOpen={(file) => {
							this.props.loadSlideDeck(file.id);
						}}
						onInjectedDelete={userIsContentManager ? null : (file) => {
							this.props.deleteSingleDeck(file.id);
						}}
						injectedColumnDefaults={{
							name: '300px',
							thumbnail: '240px',
							activeSlots: '150px',
							totalSlots: '110px',
						}}
						defaultFileViewActionId={ChonkyActions.EnableListView.id}
						hideListViewOptions={true}
					/>
				</div>
			</>
		);
	}
}

export default SlideDeckTableView;
